// src/pages/DashboardPage.js

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Typography,
  AppBar,
  Toolbar,
  CssBaseline,
  IconButton,
  useMediaQuery,
  Divider
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../hooks/useAuth';
import { getApiUrl } from '../utils/apiConfig';
import Dashboard from './Dashboard';
import Belege from './Belege';
import Finanzen from './Finanzen';
import Objekte from './Objekte';
import ToDoZuzuordnen from './ToDoZuzuordnen';
import TransactionPage from './TransactionPage';

const drawerWidth = 240;

function DashboardPage() {
  const [currentPage, setCurrentPage] = useState('Dashboard');
  const [mobileOpen, setMobileOpen] = useState(false);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleLogout = async () => {
    try {
      await fetch(getApiUrl('/api/auth/logout'), {
        method: 'POST',
        credentials: 'include',
      });
    } catch (err) {
      console.error('Logout-Anfrage fehlgeschlagen:', err);
    } finally {
      logout();
      navigate('/');
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const renderPage = () => {
    const pageStyle = {
      width: '100%',
      overflowX: 'auto',
      pl: 0,
      pr: 0
    };
    switch (currentPage) {
      case 'Dashboard':
        return (
          <Box>
            <Typography variant="h4" gutterBottom>Willkommen im Dashboard 👋</Typography>
            <Typography variant="body1" gutterBottom>Hier erhalten Sie einen Überblick über Belege, Finanzen und Objekte.</Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6">Was möchten Sie tun?</Typography>
            <ul>
              <li>Neue Belege hochladen</li>
              <li>Transaktionen importieren</li>
              <li>Mietverträge verwalten</li>
              <li>Zuordnungen abschließen</li>
              <li>Buchungen einsehen</li>
            </ul>
          </Box>
        );
      case 'Belege':
        return <Box sx={pageStyle}><Belege enableSearch showFilters /></Box>;
      case 'Finanzen':
        return <Box sx={pageStyle}><Finanzen enableSearch showFilters /></Box>;
      case 'Objekte':
        return <Box sx={pageStyle}><Objekte enableSearch showFilters /></Box>;
      case 'ToDoZuzuordnen':
        return <Box sx={pageStyle}><ToDoZuzuordnen enableSearch showFilters /></Box>;
      case 'Buchungen':
        return <Box sx={pageStyle}><TransactionPage /></Box>;
      default:
        return <Dashboard />;
    }
  };

  const drawerContent = (
    <Box sx={{ overflow: 'auto' }}>
      <Toolbar />
      <List>
        {['Dashboard', 'Belege', 'Finanzen', 'Objekte', 'ToDoZuzuordnen', 'Buchungen'].map((text) => (
          <ListItem button key={text} onClick={() => { setCurrentPage(text); if (isMobile) setMobileOpen(false); }} selected={currentPage === text}>
            <ListItemText primary={text} />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', width: '100%', overflowX: 'hidden' }}>
      <CssBaseline />
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <IconButton color="inherit" edge="start" sx={{ mr: 2 }} onClick={handleDrawerToggle}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Immo-Automation – {currentPage}
          </Typography>
          <Typography variant="body1" sx={{ cursor: 'pointer' }} onClick={handleLogout}>
            Logout
          </Typography>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="navigation"
      >
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>

      <Box component="main" sx={{ flexGrow: 1, pt: 3, width: '100%' }}>
        <Toolbar />
        {renderPage()}
      </Box>
    </Box>
  );
}

export default DashboardPage;
